import { FC } from "react";

import { Box, Stack } from "@mui/material";
import { FormCategory, Locales } from "@simplyk/common";

import { CloseDialogButton } from "./CloseDialogButton";
import { ShareFormTabLayout } from "./ShareFormTabLayout";
import { ShareFormTabTitle } from "./ShareFormTabTitle";

import Image from "@/components/Image/Image";
import { TextFieldWithCopy } from "@/components/TextFieldWithCopy/TextFieldWithCopy";
import { AmplitudeEvents } from "@/constants/amplitude";
import { EMBEDDED_FORM_SCRIPT } from "@/constants/EmbedForm";
import { useLocaleContext } from "@/contexts/LocaleContext";
import { DashboardButton } from "@/dashboard-design-system/Button/DashboardButton";
import { DashboardDivider } from "@/dashboard-design-system/Divider";
import { DashboardTypography } from "@/dashboard-design-system/Typography";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useTranslate } from "@/hooks/useTranslate";
import { ChevronLeft, ExternalLink } from "@/icons/outlined";

export type ButtonPopupInstructionsTabProps = {
  formCategory: FormCategory;
  onClose?: () => void;
  onBackClick?: () => void;
  guideLinks: Record<Locales, string>;
  embedCode: string;
};

export const ButtonPopupInstructionsTab: FC<ButtonPopupInstructionsTabProps> = ({
  embedCode,
  formCategory,
  onClose,
  onBackClick,
  guideLinks,
}) => {
  const { t } = useTranslate();
  const { locale } = useLocaleContext();
  const { isSmallScreen } = useMediaQuery();

  return (
    <ShareFormTabLayout
      slots={{
        closeButton: <CloseDialogButton onClick={onClose} />,
        header: (
          <ShareFormTabTitle>{t("dashboard", "shareFormModal.embed.choiceLinkedButton.title")}</ShareFormTabTitle>
        ),
        content: (
          <Stack gap={3}>
            <Box sx={{ textAlign: "center" }}>
              <Image
                src={
                  locale === Locales.EN
                    ? "/images/shareFormModal/button_popup_en.svg"
                    : "/images/shareFormModal/button_popup_fr.svg"
                }
                alt="Form embed"
                height={isSmallScreen ? 60 : 100}
                width={undefined}
              />
            </Box>
            <DashboardDivider />
            <Stack gap={1}>
              <DashboardTypography variant="subtitle2">
                {t("dashboard", "shareFormModal.embed.choice.ButtonPopup.label1")}
              </DashboardTypography>
              <TextFieldWithCopy
                button
                value={embedCode}
                size="medium"
                buttonCopyProps={{ vibe: "brand", variant: "filled", size: "medium" }}
                copyAmplitudeEvent={AmplitudeEvents.ShareFormModalClickCopyEmbed}
                copyAmplitudePayload={{
                  form_embed_type: "ButtonPopup",
                  property: "button-property",
                  form_type: formCategory,
                }}
              />
            </Stack>
            <Stack gap={1}>
              <DashboardTypography variant="subtitle2">
                {t("dashboard", "shareFormModal.embed.choice.ButtonPopup.label2")}
              </DashboardTypography>
              <TextFieldWithCopy
                button
                value={EMBEDDED_FORM_SCRIPT}
                size="medium"
                buttonCopyProps={{ vibe: "brand", variant: "filled", size: "medium" }}
                copyAmplitudeEvent={AmplitudeEvents.ShareFormModalClickCopyEmbed}
                copyAmplitudePayload={{
                  form_embed_type: "ButtonPopup",
                  property: "button-script",
                  form_type: formCategory,
                }}
              />
            </Stack>
            <Stack gap={1} sx={{ alignSelf: "center" }}>
              <DashboardTypography variant="subtitle2">
                {t("dashboard", "shareFormModal.embed.choice.extraHelp")}
              </DashboardTypography>
              <DashboardButton
                vibe="brand"
                variant="outlined"
                size="medium"
                endIcon={<ExternalLink />}
                href={guideLinks[locale]}
                target="_blank"
              >
                {t("dashboard", "shareFormModal.embed.choice.visitOurGuide")}
              </DashboardButton>
            </Stack>
          </Stack>
        ),
        footer: (
          <Stack direction="row" alignItems="center" justifyContent="flex-start">
            <DashboardButton
              vibe="neutral"
              variant="outlined"
              size="large"
              startIcon={<ChevronLeft />}
              onClick={onBackClick}
            >
              {t("common", "back")}
            </DashboardButton>
          </Stack>
        ),
      }}
    />
  );
};
